import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import ReportOutlined from '@mui/icons-material/ReportOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import { Box, Stack, Typography } from '@mui/material';

export interface ValidationSurfaceProps {
  /** Publish-blocking messages (red region). */
  errors: string[];
  /** Advisory messages (amber region). */
  warnings: string[];
  labels: {
    /** "Errors" heading. */
    errors: string;
    /** "Warnings" heading. */
    warnings: string;
    /** Right-aligned caption on the errors region, e.g. "blocks publish". */
    blocksPublish: string;
    /** Right-aligned caption on the warnings region, e.g. "advisory". */
    advisory: string;
    /** All-clear message, e.g. "No issues — ready to publish." */
    clear: string;
  };
}

function region(kind: 'error' | 'warning', list: string[], heading: string, sideCaption: string) {
  const paletteKey = kind;
  return (
    <Box
      key={kind}
      sx={(theme: Theme) => ({
        borderRadius: '8px',
        border: `1px solid ${alpha(theme.palette[paletteKey].main, kind === 'error' ? 0.4 : 0.36)}`,
        bgcolor: alpha(theme.palette[paletteKey].main, kind === 'error' ? 0.07 : 0.09),
        overflow: 'hidden',
      })}
    >
      <Box
        sx={(theme: Theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.75,
          py: 1,
          borderBottom: `1px solid ${alpha(theme.palette[paletteKey].main, 0.2)}`,
        })}
      >
        {kind === 'error' ? (
          <ReportOutlined sx={{ fontSize: 15, color: 'error.dark' }} />
        ) : (
          <WarningAmberOutlined sx={{ fontSize: 15, color: 'warning.dark' }} />
        )}
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            color: `${paletteKey}.dark`,
          }}
        >
          {heading}
        </Typography>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary' }}>
          {list.length}
        </Typography>
        <Typography sx={{ ml: 'auto', fontSize: 11, color: 'text.secondary' }}>
          {sideCaption}
        </Typography>
      </Box>
      <Box
        component="ul"
        sx={{ m: 0, py: 1, pl: 3.75, pr: 1.75, display: 'flex', flexDirection: 'column', gap: 0.5 }}
      >
        {list.map((message, i) => (
          <Typography
            key={i}
            component="li"
            sx={{ fontSize: 12.5, fontWeight: 500, color: 'text.primary' }}
          >
            {message}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

/**
 * The template editor's validation surface: publish-blocking Errors (red) and
 * advisory Warnings (amber) as visually distinct regions, or a green all-clear
 * row when both are empty. Port of the prototype `NteValidation`.
 */
export function ValidationSurface({ errors, warnings, labels }: ValidationSurfaceProps) {
  return (
    <Stack spacing={1.25}>
      {errors.length > 0 ? region('error', errors, labels.errors, labels.blocksPublish) : null}
      {warnings.length > 0 ? region('warning', warnings, labels.warnings, labels.advisory) : null}
      {errors.length === 0 && warnings.length === 0 ? (
        <Box
          sx={(theme: Theme) => ({
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.75,
            py: 1.125,
            borderRadius: '8px',
            bgcolor: alpha(theme.palette.success.main, 0.08),
            border: `1px solid ${alpha(theme.palette.success.main, 0.32)}`,
          })}
        >
          <CheckCircleOutlined sx={{ fontSize: 15, color: 'success.dark' }} />
          <Typography sx={{ fontSize: 12.5, fontWeight: 500, color: 'text.primary' }}>
            {labels.clear}
          </Typography>
        </Box>
      ) : null}
    </Stack>
  );
}
